import { authAPI } from "../api/client";
import { useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import LoadingButton from "../components/LoadingButton";
import { useMsal } from "@azure/msal-react";
import { trackLogin } from "../utils/analytics";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import "./Login.css";

// Microsoft's official four-square mark, inlined so it renders crisply and
// needs no network request. Decorative: the button already carries the label
// text, so this is hidden from assistive tech rather than announced as
// "Microsoft logo" on top of "Sign in with Microsoft".
function MicrosoftMark() {
  return (
    <svg
      className="microsoft-mark"
      viewBox="0 0 23 23"
      width="18"
      height="18"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

export default function Login({ onLoginSuccess }) {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const [isMicrosoftLoginLoading, setIsMicrosoftLoginLoading] = useState(false);
  const [loginMessage, setLoginMessage] = useState("");

  /* ================= MICROSOFT LOGIN ================= */
  const handleMicrosoftLogin = async () => {
    if (isMicrosoftLoginLoading) return;

    try {
      setIsMicrosoftLoginLoading(true);
      setLoginMessage("");
      const loginRequest = { scopes: ["User.Read"] };
      let accessToken;
      let account = instance.getActiveAccount() || instance.getAllAccounts()[0];

      if (account) {
        try {
          const silentTokenResponse = await instance.acquireTokenSilent({
            ...loginRequest,
            account,
          });
          accessToken = silentTokenResponse.accessToken;
        } catch (_) {
          // Fallback to popup when silent token acquisition fails.
        }
      }

      if (!accessToken) {
        const loginResponse = await instance.loginPopup({
          ...loginRequest,
          prompt: "select_account",
        });

        account = loginResponse.account;
        if (!account) {
          setLoginMessage("No Microsoft account selected.");
          return;
        }

        instance.setActiveAccount(account);
        accessToken = loginResponse.accessToken;

        if (!accessToken) {
          const tokenResponse = await instance.acquireTokenSilent({
            ...loginRequest,
            account,
          });
          accessToken = tokenResponse.accessToken;
        }
      }

      if (!accessToken) {
        throw new Error("Microsoft access token missing from login response");
      }

      const response = await authAPI.microsoftLogin(accessToken);

      if (response.data.token && response.data.user) {
        localStorage.setItem("token", response.data.token);
        if (response.data.refreshToken) localStorage.setItem("refreshToken", response.data.refreshToken);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        sessionStorage.removeItem("hrportal_last_path");
        // A different user may have been logged in before: drop the previous
        // user's cached matrix BEFORE the App snapshot reload runs, or the
        // sidebar can briefly render (and stick on) stale permissions.
        try {
          localStorage.removeItem("hrportal_user_access_v3");
          localStorage.removeItem("hrportal_user_access_v2");
          localStorage.removeItem("hrportal_user_access");
        } catch {
          // ignore storage errors
        }
        window.dispatchEvent(new Event("hrportal-auth"));
        trackLogin("microsoft");
        onLoginSuccess();
        // Common landing page for every authenticated user. Role-based
        // dashboards remain reachable from Home, the sidebar and direct URLs.
        navigate("/home", { replace: true });
      }
    } catch (err) {
      console.error("Microsoft login failed:", err);

      if (err?.response?.data?.error === "USER_NOT_REGISTERED") {
        setLoginMessage("Your email is not registered. Please contact admin.");
        return;
      }

      if (err?.response?.data?.error === "ACCOUNT_DISABLED") {
        setLoginMessage("Your account has been disabled. Please contact HR Manager.");
        return;
      }

      if (err.errorCode === "user_cancelled") {
        return;
      }

      if (err?.response?.data?.error === "MS_TEMPORARY_ERROR") {
        setLoginMessage("Microsoft service is temporarily unavailable. Please try again.");
        return;
      }

      if (err?.code === "ERR_NETWORK" || !err?.response) {
        setLoginMessage(
          "Cannot reach the HR server. The API may be down — contact your admin or try again later."
        );
        return;
      }

      setLoginMessage("Microsoft login failed. Please try again.");
    } finally {
      setIsMicrosoftLoginLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg" aria-hidden="true">
        <span className="login-glow login-glow--teal" />
        <span className="login-glow login-glow--blue" />
        <span className="login-arc login-arc--a" />
        <span className="login-arc login-arc--b" />
        <span className="login-arc login-arc--c" />
        <span className="login-dots login-dots--tr" />
        <span className="login-dots login-dots--bl" />
      </div>

      <main className="login-content">

        <header className="login-brand">
          <img loading="lazy"
            src={require("../assest/login-brand.png")}
            alt="Univision Technology Solutions"
            className="login-logo-img"
          />
          <h1 className="login-logo-title">HR Management System</h1>
          <p className="login-logo-subtitle">
            Recruitment, people operations and support in one place
          </p>
        </header>

        <section className="login-card">
          <h2 className="login-title">Welcome back</h2>
          <p className="login-subtitle">Sign in with your UTS work account</p>

          {loginMessage && (
            <p className="login-alert" role="alert">
              <span className="login-alert-icon" aria-hidden="true">!</span>
              <span>{loginMessage}</span>
            </p>
          )}

          <LoadingButton
            className="microsoft-btn"
            onClick={handleMicrosoftLogin}
            disabled={isMicrosoftLoginLoading}
            loading={isMicrosoftLoginLoading}
            loadingText="Signing in..."
          >
            <MicrosoftMark />
            <span className="microsoft-btn-label">Continue with Microsoft</span>
            <ChevronRightRoundedIcon
              className="microsoft-btn-chevron"
              fontSize="small"
            />
          </LoadingButton>

          <div className="login-divider">
            <span className="login-divider-text">or</span>
          </div>

          <Link to="/admin" className="sudo-btn">
            <LockOutlinedIcon fontSize="small" />
            <span>Sudo Access</span>
          </Link>
        </section>

        <footer className="login-footer">
          <ul className="login-features">
            <li className="login-feature">
              <ShieldOutlinedIcon fontSize="small" />
              <span>Secure Access</span>
            </li>
            <li className="login-feature-sep" aria-hidden="true" />
            <li className="login-feature">
              <GroupsOutlinedIcon fontSize="small" />
              <span>People Operations</span>
            </li>
            <li className="login-feature-sep" aria-hidden="true" />
            <li className="login-feature">
              <BarChartOutlinedIcon fontSize="small" />
              <span>Recruitment Support</span>
            </li>
          </ul>
          <p className="login-copyright">Univision Technology Solutions</p>
        </footer>

      </main>
    </div>
  );
}
